/**
 * THE ROSTER, READ — every derived fact about who is on this call, as one pure
 * function of the participant list and my own id.
 *
 * WHY IT IS NOT IN THE HOOK ANY MORE, and this is the whole reason the file
 * exists. `useCallParties` computed all of this inside a `useMemo`, where the
 * only way to reach it is to render a component that needs a query client, a
 * router and a live socket — so nothing did. An independent mutation probe
 * disclosed `answered` as untested; a later one proved it, and then proved
 * something worse: with the rule extracted but the WIRING still in the memo,
 *
 *     answered: canOfferEscalation(live)     // instead of `counterparts`
 *
 * typechecks, lints, and passes every test in the suite. `live` includes MY OWN
 * connected leg, which is `connected` from the first second of the call — so
 * that one word puts "Move to meeting" back on the menu for the whole 45-second
 * ring, which is the exact defect A-F1's client half was written to remove. The
 * rule was pinned and the wiring was not, so the rule could be bypassed without
 * being touched.
 *
 * Everything the hook still does is genuinely hook work: fetch, subscribe,
 * invalidate, retry. Everything that is a DECISION about the roster is here,
 * where a test can hold it.
 */
import type { CallParticipant } from '@cyphex/types/calls';
import type { UserId } from '@cyphex/types';

import { canOfferEscalation } from './escalation';

/** A leg that is on the call in some form — ringing included. */
const LIVE: ReadonlySet<string> = new Set(['connected', 'on_hold', 'ringing']);

const nameOf = (party: CallParticipant): string => party.user?.displayName ?? 'Someone';

/**
 * WHO THE HEADER NAMES MUST NOT MOVE.
 *
 * `GET /calls/:id` returns participants in whatever order Postgres hands them
 * back, and an UPDATE moves a row — so the instant somebody pressed hold, the
 * heap order changed and the header renamed the call from "Ganesh and 2 others"
 * to "Priya and 2 others". Observed in the browser proof. Ordering by when each
 * person picked up (id as the tie-break, and for legs still ringing) makes the
 * name a property of the call rather than of the last write to it.
 */
function byArrival(a: CallParticipant, b: CallParticipant): number {
  const at = a.answeredAt ?? '';
  const bt = b.answeredAt ?? '';
  if (at !== bt) return at < bt ? -1 : 1;
  return a.id < b.id ? -1 : 1;
}

/** Teams' own summary: one name, or a name plus a count. Never a list. */
export function labelFor(counterparts: readonly CallParticipant[]): string {
  const first = counterparts[0];
  if (first === undefined) return 'Call';
  if (counterparts.length === 1) return nameOf(first);
  return `${nameOf(first)} and ${counterparts.length - 1} other${
    counterparts.length === 2 ? '' : 's'
  }`;
}

export interface CallPartiesView {
  counterparts: CallParticipant[];
  label: string;
  heldBy: CallParticipant[];
  liveCount: number;
  ringing: boolean;
  answered: boolean;
  rosterUserIds: UserId[];
}

/**
 * `meId` is `null` when the session has not resolved yet, and that case has to
 * stay HONEST rather than convenient: with no id, nobody can be told apart from
 * me, so every leg is a "counterpart" and `answered` would go true on my own
 * connected leg alone. The whole list is treated as unknown instead — the same
 * answer an empty roster gets, and the same one the erroring roster gets.
 */
export function callPartiesView(
  parties: readonly CallParticipant[],
  meId: string | null,
): CallPartiesView {
  if (meId === null || meId.length === 0) {
    return {
      counterparts: [],
      label: 'Call',
      heldBy: [],
      liveCount: 0,
      ringing: false,
      answered: false,
      rosterUserIds: [],
    };
  }

  const live = parties.filter((party) => LIVE.has(party.state));
  const counterparts = live.filter((party) => party.user?.id !== meId).sort(byArrival);

  return {
    counterparts,
    label: labelFor(counterparts),
    heldBy: counterparts.filter((party) => party.state === 'on_hold'),
    liveCount: live.length,
    ringing: counterparts.some((party) => party.state === 'ringing'),
    /* COUNTERPARTS, never `live`. `live` carries my own leg, which the server
       writes `connected` at dial time (services/callRouting.ts), so asking it
       would answer "yes, somebody has picked up" about MYSELF for the whole
       ring. `canOfferEscalation` holds the rest of the rule. */
    answered: canOfferEscalation(counterparts),
    rosterUserIds: [
      ...new Set(
        parties.flatMap((party) =>
          party.user === null || party.user.id === meId ? [] : [party.user.id],
        ),
      ),
    ],
  };
}
